import { Box, Group, NumberInput, Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { roundedNumericFormatter } from "../../../utils/RoundedNumberFormat";
import { OrangeCircle } from "../../OrangeCircle/OrangeCircle";
import { MarketingTypeDeleteModalContent } from "../MarketingTypeTable/MarketingTypeDeleteModalContent";
import {
  PeriodBasedTable,
  TableCellErrorText,
  TableCellWarningText
} from "../PeriodBasedTable/PeriodBasedTable";
import { EMPTY_DATE_PLACEHOLDER } from "../PeriodBasedTable/PeriodBasedTable.constants";
import type {
  ExternalErrors,
  PeriodBasedRow,
  ValueCellProps,
  ValueEditProps
} from "../PeriodBasedTable/PeriodBasedTable.types";
import { validateAZWValue } from "../utils/validateAzwValue";
import { CompensationRateEditModalContent } from "./CompensationRateEditModalContent";

export type CompensationValueField = { price_ct_per_kwh: string | null };
export type CompensationRateRow = PeriodBasedRow<CompensationValueField>;

export type CompensationRateCellProps =
  ValueCellProps<CompensationValueField> & {
    highlightMissing?: boolean;
  };

export const CompensationRateCell = ({
  row,
  error,
  highlightMissing = false
}: CompensationRateCellProps) => {
  const hasPriceValue =
    row.price_ct_per_kwh !== undefined &&
    row.price_ct_per_kwh !== "" &&
    row.price_ct_per_kwh !== null;

  return (
    <Box>
      <Group gap="xs" wrap="nowrap">
        {!hasPriceValue && !error && highlightMissing && <OrangeCircle />}
        <Text span>
          {hasPriceValue
            ? `${roundedNumericFormatter(row.price_ct_per_kwh)} ct/kWh`
            : `${EMPTY_DATE_PLACEHOLDER} ct/kWh`}
        </Text>
      </Group>
      <TableCellErrorText error={error} />
    </Box>
  );
};

export const CompensationRateEdit = ({
  value,
  onChange,
  error
}: ValueEditProps<CompensationValueField>) => {
  const [localValue, setLocalValue] = useState<string | number>(value ?? "");
  const warning = validateAZWValue(localValue);

  return (
    <Box>
      <NumberInput
        decimalScale={2}
        error={error}
        placeholder="z. B. 8,82 ct/kWh"
        suffix=" ct/kWh"
        value={localValue}
        onChange={(newValue) => {
          setLocalValue(newValue ?? "");
          onChange(newValue);
        }}
      />
      <TableCellWarningText warning={warning} />
    </Box>
  );
};

export interface CompensationRateTableProps {
  periodBasedData: CompensationRateRow[];
  onChange: (updatedRows: CompensationRateRow[]) => void;
  commissioningDate?: string;
  generatorId?: number;
  readOnly?: boolean;
  variantId?: number;
  isMarktpraemie?: boolean;
  externalErrors?: ExternalErrors;
  highlightMissing?: boolean;
}

export function CompensationRateTable({
  periodBasedData,
  onChange,
  commissioningDate,
  generatorId,
  readOnly = false,
  variantId,
  isMarktpraemie = false,
  externalErrors,
  highlightMissing = false
}: CompensationRateTableProps) {
  const initialData = useMemo(() => {
    if (periodBasedData && periodBasedData.length === 1) {
      const firstRow = periodBasedData[0];
      if (firstRow.price_ct_per_kwh === "" && commissioningDate) {
        return [
          {
            ...firstRow,
            first_date: commissioningDate,
            price_ct_per_kwh: "0"
          }
        ];
      }
    }

    if (periodBasedData && periodBasedData.length > 0) {
      return periodBasedData;
    }
    if (commissioningDate) {
      return [
        {
          first_date: commissioningDate,
          last_date: null,
          price_ct_per_kwh: "0"
        }
      ];
    }
    return [];
  }, [periodBasedData, commissioningDate]);

  useEffect(() => {
    const hasEmptyInitialPrice =
      periodBasedData?.length === 1 &&
      periodBasedData[0].price_ct_per_kwh === "" &&
      commissioningDate;

    if (
      ((!periodBasedData || periodBasedData.length === 0) &&
        commissioningDate &&
        initialData.length > 0) ||
      hasEmptyInitialPrice
    ) {
      onChange(initialData);
    }
  }, [periodBasedData, commissioningDate, initialData, onChange]);

  const createRow = useCallback(
    (
      values: Record<string, string | number | null>
    ): PeriodBasedRow<CompensationValueField> => {
      return {
        first_date: (values.first_date as string) || "",
        last_date: (values.last_date as string | null) || null,
        price_ct_per_kwh:
          values.price_ct_per_kwh === ""
            ? null
            : (values.price_ct_per_kwh as string)
      };
    },
    []
  );

  return (
    <PeriodBasedTable
      createRow={createRow}
      DeleteModalBody={(row) => (
        <MarketingTypeDeleteModalContent // CompensationRateTable uses the same delete modal as MarketingTypeTable
          generatorId={generatorId}
          row={row}
          variantId={variantId}
        />
      )}
      deleteModalDataTypeName={
        isMarktpraemie ? "den Anzulegenden Wert" : "die EEG Vergütung"
      }
      EditModalBody={(row) => (
        <CompensationRateEditModalContent
          generatorId={generatorId}
          row={row}
          variantId={variantId}
        />
      )}
      editModalDataTypeName={
        isMarktpraemie ? "den Anzulegenden Wert" : "die EEG Vergütung"
      }
      enableDeleteForRow={
        commissioningDate
          ? (_row, index, allRows) => {
              // First row cannot be deleted
              if (index === 0) return false;
              return allRows.length > 1;
            }
          : undefined
      }
      externalErrors={externalErrors}
      periodBasedData={initialData}
      readOnly={readOnly}
      valueColumn={{
        header: isMarktpraemie
          ? "AZW (+ Zuschläge)"
          : "Einspeisevergütung (+ Zuschläge)",
        validationLabel: isMarktpraemie ? "AZW" : "Einspeisevergütung",
        fieldName: "price_ct_per_kwh",
        CellComponent: (props) => (
          <CompensationRateCell
            {...props}
            highlightMissing={highlightMissing && !readOnly}
          />
        ),
        EditComponent: CompensationRateEdit
      }}
      onChange={onChange}
    />
  );
}
